import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EXAM_TYPES, APPOINTMENT_STATUSES, type SurveillanceOccurrence } from '@toxofollow/shared';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Table, Field, inputClass } from '../components/ui.js';
import { Select, Textarea, FormGrid, QueryState, ErrorState } from '../components/form.js';
import { TrendChart, type TrendPoint } from '../components/charts.js';
import { ImagingImport } from '../components/ImagingImport.js';

interface AppointmentRow {
  id: string;
  date: string;
  examType: string;
  specialist: string | null;
  reason: string | null;
  findings: string | null;
  status: string;
  nextDate: string | null;
  headCircumferenceCm: string | null;
  signedAt: string | null;
  parentConfirmation: string | null;
}
interface GrowthRow {
  id: string;
  date: string;
  weightGrams: string | null;
  weightSource: string | null;
  weightVerified: boolean;
  headCircumferenceCm: string | null;
  note: string | null;
}

function surveillanceTone(s: SurveillanceOccurrence['status']): 'info' | 'warn' | 'danger' | 'ok' {
  if (s === 'overdue') return 'danger';
  if (s === 'due') return 'warn';
  if (s === 'done') return 'ok';
  return 'info';
}

export function Appointments() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const apptsQ = useQuery({ queryKey: ['appointments'], queryFn: () => api.get<{ appointments: AppointmentRow[] }>('/appointments') });
  const survQ = useQuery({ queryKey: ['surveillance'], queryFn: () => api.get<{ occurrences: SurveillanceOccurrence[] }>('/surveillance') });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-800">{t('appt.title')}</h1>
        <Button onClick={() => setShowForm((s) => !s)}>{showForm ? t('common.cancel') : `+ ${t('exam.pediatric_consult')}`}</Button>
      </div>

      {showForm && <AppointmentForm onDone={() => setShowForm(false)} />}

      {/* --- Import an ETF imaging report --- */}
      <Card title={t('appt.importEtf')}>
        <ImagingImport
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['appointments'] });
            void qc.invalidateQueries({ queryKey: ['surveillance'] });
          }}
        />
      </Card>

      {/* --- 12-month surveillance calendar --- */}
      <Card title={t('appt.surveillanceCalendar')}>
        <QueryState
          isLoading={survQ.isLoading}
          isError={survQ.isError}
          error={survQ.error}
          errorFallback={t('common.error')}
          isEmpty={(survQ.data?.occurrences.length ?? 0) === 0}
          emptyMessage="—"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {survQ.data?.occurrences.map((occ, i) => (
              <div key={i} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <div>
                  <div className="font-medium text-slate-700">{t(`exam.${occ.examType}`)}</div>
                  <div className="text-xs text-slate-400">{occ.dueDate}{occ.phaseLabel ? ` · ${occ.phaseLabel}` : ''}</div>
                </div>
                <Badge tone={surveillanceTone(occ.status)}>{t(`surv.${occ.status}`)}</Badge>
              </div>
            ))}
          </div>
        </QueryState>
      </Card>

      {/* --- Appointments list --- */}
      <Card title={t('appt.appointments')}>
        <QueryState
          isLoading={apptsQ.isLoading}
          isError={apptsQ.isError}
          error={apptsQ.error}
          errorFallback={t('common.error')}
          isEmpty={(apptsQ.data?.appointments.length ?? 0) === 0}
          emptyMessage="—"
        >
          <div className="space-y-3">
            {apptsQ.data?.appointments.map((a) => (
              <AppointmentRowView key={a.id} appt={a} />
            ))}
          </div>
        </QueryState>
      </Card>

      {/* --- Growth --- */}
      <Growth />
    </div>
  );
}

function AppointmentRowView({ appt }: { appt: AppointmentRow }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [findings, setFindings] = useState(appt.findings ?? '');
  const [headCircumferenceCm, setHc] = useState(appt.headCircumferenceCm ?? '');
  const [nextDate, setNextDate] = useState(appt.nextDate ?? '');
  const [status, setStatus] = useState(appt.status);

  const m = useMutation({
    mutationFn: () =>
      api.put(`/appointments/${appt.id}`, {
        findings: findings.trim() || undefined,
        headCircumferenceCm: headCircumferenceCm.trim() || undefined,
        nextDate: nextDate || undefined,
        status,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
      void qc.invalidateQueries({ queryKey: ['surveillance'] });
      setEditing(false);
    },
  });

  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <strong>{appt.date}</strong>
          <span className="font-medium">{t(`exam.${appt.examType}`)}</span>
          {appt.specialist && <span className="text-slate-400">{appt.specialist}</span>}
          <Badge tone={appt.status === 'completed' ? 'ok' : appt.status === 'missed' ? 'danger' : 'info'}>{t(`appt.status.${appt.status}`)}</Badge>
          {/* Family-reported booking/attendance — prompts the clinician to verify + sign. */}
          {appt.parentConfirmation && (
            <Badge tone={appt.parentConfirmation === 'attended' && appt.status !== 'completed' ? 'warn' : 'info'}>
              {appt.parentConfirmation === 'attended' && appt.status !== 'completed'
                ? t('appt.parentReportsAttended')
                : t(`apptConfirm.${appt.parentConfirmation}`)}
            </Badge>
          )}
          {appt.signedAt && <span className="text-xs text-green-700">✓ {t('appt.signed')}</span>}
        </div>
        <Button variant="ghost" onClick={() => setEditing((e) => !e)}>{editing ? t('common.cancel') : t('common.edit')}</Button>
      </div>
      {appt.findings && !editing && <p className="text-sm text-slate-600 mt-1">{appt.findings}</p>}
      {appt.headCircumferenceCm && !editing && <p className="text-xs text-slate-400">{t('growth.headCircumference')}: {appt.headCircumferenceCm} cm</p>}
      {appt.nextDate && !editing && <p className="text-xs text-slate-400">{t('common.next')}: {appt.nextDate}</p>}

      {editing && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <Field label={t('appt.findings')}>
            <Textarea rows={2} value={findings} onChange={(e) => setFindings(e.target.value)} />
          </Field>
          <FormGrid>
            <Field label={`${t('growth.headCircumference')} (cm)`}>
              <input className={inputClass} inputMode="decimal" value={headCircumferenceCm} onChange={(e) => setHc(e.target.value)} />
            </Field>
            <Field label={t('appt.nextDate')}>
              <input className={inputClass} type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
            </Field>
            <Field label={t('common.status')}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                {APPOINTMENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`appt.status.${s}`)}</option>
                ))}
              </Select>
            </Field>
          </FormGrid>
          {m.isError && <div className="mb-2"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
          <Button onClick={() => m.mutate()} disabled={m.isPending}>{t('common.save')}</Button>
        </div>
      )}
    </div>
  );
}

function AppointmentForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [examType, setExamType] = useState('pediatric_consult');
  const [specialist, setSpecialist] = useState('');
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState('scheduled');

  const m = useMutation({
    mutationFn: () =>
      api.post('/appointments', {
        date,
        examType,
        specialist: specialist.trim() || undefined,
        reason: reason.trim() || undefined,
        status,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
      void qc.invalidateQueries({ queryKey: ['surveillance'] });
      onDone();
    },
  });

  return (
    <Card title={`+ ${t('exam.pediatric_consult')}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <FormGrid>
          <Field label={t('common.date')}>
            <input className={inputClass} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </Field>
          <Field label={t('appt.examType')}>
            <Select value={examType} onChange={(e) => setExamType(e.target.value)}>
              {EXAM_TYPES.map((x) => (
                <option key={x} value={x}>{t(`exam.${x}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('appt.specialist')}>
            <input className={inputClass} value={specialist} onChange={(e) => setSpecialist(e.target.value)} />
          </Field>
          <Field label={t('common.status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              {APPOINTMENT_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`appt.status.${s}`)}</option>
              ))}
            </Select>
          </Field>
        </FormGrid>
        <Field label={t('appt.reason')}>
          <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {m.isError && <div className="mb-3"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
        <div className="flex gap-2">
          <Button type="submit" disabled={m.isPending}>{t('common.save')}</Button>
          <Button type="button" variant="secondary" onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

function Growth() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const q = useQuery({ queryKey: ['growth'], queryFn: () => api.get<{ growth: GrowthRow[] }>('/growth') });

  const [date, setDate] = useState(today);
  const [weightGrams, setWeight] = useState('');
  const [weightSource, setSource] = useState('clinic');
  const [weightVerified, setVerified] = useState(false);
  const [headCircumferenceCm, setHc] = useState('');

  const createM = useMutation({
    mutationFn: () =>
      api.post('/growth', {
        date,
        weightGrams: weightGrams.trim() || undefined,
        weightSource: weightGrams.trim() ? weightSource : undefined,
        weightVerified,
        headCircumferenceCm: headCircumferenceCm.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['growth'] });
      setWeight('');
      setHc('');
    },
  });
  const verifyM = useMutation({
    mutationFn: (id: string) => api.post(`/growth/${id}/verify`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['growth'] }),
  });

  const rows = [...(q.data?.growth ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  const weightPoints: TrendPoint[] = rows.filter((r) => r.weightGrams).map((r) => ({ date: r.date, value: Number(r.weightGrams) }));
  const hcPoints: TrendPoint[] = rows.filter((r) => r.headCircumferenceCm).map((r) => ({ date: r.date, value: Number(r.headCircumferenceCm) }));

  return (
    <Card title={t('growth.weight') + ' / ' + t('growth.headCircumference')}>
      <form
        className="flex flex-wrap items-end gap-3 mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          createM.mutate();
        }}
      >
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">{t('common.date')}</span>
          <input className={inputClass} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">{t('growth.weight')} (g)</span>
          <input className={inputClass} inputMode="decimal" value={weightGrams} onChange={(e) => setWeight(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">{t('common.source')}</span>
          <Select value={weightSource} onChange={(e) => setSource(e.target.value)}>
            {(['parent', 'clinic', 'hospital', 'pharmacy', 'imported'] as const).map((s) => (
              <option key={s} value={s}>{t(`weight.source.${s}`)}</option>
            ))}
          </Select>
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">{t('growth.headCircumference')} (cm)</span>
          <input className={inputClass} inputMode="decimal" value={headCircumferenceCm} onChange={(e) => setHc(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" checked={weightVerified} onChange={(e) => setVerified(e.target.checked)} />
          {t('weight.verified')}
        </label>
        <Button type="submit" disabled={createM.isPending}>{t('common.save')}</Button>
      </form>
      {createM.isError && <div className="mb-3"><ErrorState error={createM.error} fallback={t('common.error')} /></div>}

      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        errorFallback={t('common.error')}
        isEmpty={rows.length === 0}
        emptyMessage="—"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {weightPoints.length >= 1 && (
            <div className="border border-slate-200 rounded-lg p-3">
              <h3 className="font-medium text-slate-700 mb-2">{t('growth.weight')} (g)</h3>
              <TrendChart data={weightPoints} unit="g" color="#2563eb" />
            </div>
          )}
          {hcPoints.length >= 1 && (
            <div className="border border-slate-200 rounded-lg p-3">
              <h3 className="font-medium text-slate-700 mb-2">{t('growth.headCircumference')} (cm)</h3>
              <TrendChart data={hcPoints} unit="cm" color="#7c3aed" />
            </div>
          )}
        </div>

        <Table headers={[t('common.date'), t('growth.weight'), t('common.source'), t('growth.headCircumference'), '']}>
          {[...rows].reverse().map((r) => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="py-2 pr-4">{r.date}</td>
              <td className="py-2 pr-4">
                {r.weightGrams ? `${r.weightGrams} g` : '—'}{' '}
                {r.weightGrams && (r.weightVerified ? <Badge tone="ok">{t('weight.verified')}</Badge> : <Badge tone="warn">{t('weight.unverified')}</Badge>)}
              </td>
              <td className="py-2 pr-4 text-slate-500">{r.weightSource ? t(`weight.source.${r.weightSource}`) : '—'}</td>
              <td className="py-2 pr-4">{r.headCircumferenceCm ? `${r.headCircumferenceCm} cm` : '—'}</td>
              <td className="py-2 pr-4">
                {r.weightGrams && !r.weightVerified && (
                  <button className="text-xs underline text-brand" onClick={() => verifyM.mutate(r.id)}>{t('weight.verified')}</button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </QueryState>
    </Card>
  );
}
